// src/components/CrowdData.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axiosInstance from '../api/axiosInstance'; // Import axiosInstance
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts'; // ScatterChart import removed as it's not used
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import TimelineForm from './TimelineForm'; // Import the form
// import CaseStatusTimelineChart from './CaseStatusTimelineChart'; // Keep if you re-add this chart later
import PublicTimelineTable from './PublicTimelineTable'; // Import the table component for public timelines
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Modal,
  CircularProgress,
  Alert,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
  AlertTitle,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download'; // Changed from ShareIcon
import CampaignIcon from '@mui/icons-material/Campaign'; // Added for banner icon
// import CloseIcon from '@mui/icons-material/Close'; // Removed - Alert's onClose handles it
import { getEchartsWatermark, handleEchartsDownload } from '../utils/echartsUtils'; // Import ECharts utilities
import { formatDrawName } from '../utils/drawUtils'; // Import the centralized formatter

import CaseStatusTimelineChart from './CaseStatusTimelineChart'; 

// Register necessary ECharts components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  DataZoomComponent,
  BarChart, // ScatterChart removed
  CanvasRenderer
]);

// Removed unused imports: styled, LockOutlinedIcon, Grid

// Removed ChartPlaceholder comment - using ReactECharts directly
// Removed local STREAM_NAME_ALIASES and getStreamAlias function - using formatDrawName

const CrowdData = ({ user, userTimelineData, existingTimelineData, onUpdateClick, onDirectSubmit, onSubmissionSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [aggregateData, setAggregateData] = useState(null);
  const [voAggregateData, setVoAggregateData] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [errorCharts, setErrorCharts] = useState('');
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isWatching, setIsWatching] = useState(true); // Start by watching charts by default

  const initialBlankFormData = useMemo(() => ({ // Wrap in useMemo for stability if needed elsewhere
    applicationStream: '',
    province: '',
    isExpressEntry: false,
    nocCode: '',
    voOffice: '',
    itaDate: null,
    applicationSubmittedDate: null,
    aorDate: null,
    biometricsInstructionLetterDate: null,
    biometricsSubmittedDate: null,
    medicalExamInstructionDate: null,
    medicalExamPassedDate: null,
    backgroundCheckStartDate: null,
    securityCheck: false,
    birthCountry: '',
    finalDecisionDate: null,
    portal1Date: null,
    portal2Date: null,
    eCoprDate: null,
    notes: '',
    pnpProvince: '',
    pnpNominationDate: null,
    isPnp: false,
  }), []); // Empty dependency array, initial state is stable

  const [individualTimelineData, setIndividualTimelineData] = useState([]);

  const streamEchartsRef = useRef(null);
  const voEchartsRef = useRef(null);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const processingTimeNote = 'Note: Average processing time is calculated from the AOR (Acknowledgement of Receipt) date to the eCoPR date unlike the IRCC which uses Final Decision date.';

  const [selectedStreams, setSelectedStreams] = useState([]);
  const [selectedVOs, setSelectedVOs] = useState([]); // Added state for VO filter

  const availableStreams = useMemo(() => {
      // Static list, could be derived from aggregateData if preferred
      return [
          'Provincial Nominee Program', 'French language proficiency', 'Canadian Experience Class',
          'Healthcare occupations', 'Trade occupations', 'General', 'STEM occupations',
          'Transport occupations', 'Agriculture and agri-food occupations', 'No Program Specified',
          'Federal Skilled Worker', 'Federal Skilled Trades'
      ].sort(); // Sort alphabetically
  }, []);

  const [formData, setFormData] = useState(initialBlankFormData);

  // Derive available visa offices from public data for filtering
  const availableVOs = useMemo(() => {
    if (!individualTimelineData || !Array.isArray(individualTimelineData)) return [];
    const uniqueVOs = [...new Set(individualTimelineData.map(item => item.voOffice).filter(Boolean))];
    return uniqueVOs.sort(); // Sort alphabetically
  }, [individualTimelineData]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingCharts(true);
      setErrorCharts('');
      try {
        const [aggResponse, voResponse, individualResponse] = await Promise.all([
          axiosInstance.get('/api/timeline/aggregate'),
          axiosInstance.get('/api/timeline/aggregate/by-vo'),
          axiosInstance.get('/api/timeline/public-data-big')
        ]);

        const aggData = aggResponse.data;
        const voData = voResponse.data;
        const indData = individualResponse.data;

        setAggregateData(aggData);
        setVoAggregateData(voData);
        setIndividualTimelineData(indData);
        // console.log("Fetched aggregate data:", aggData);
        // console.log("Fetched VO aggregate data:", voData);
        // console.log("Fetched individual timeline data:", indData);

      } catch (err) {
        console.error("Error fetching crowd data:", err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to load community timeline data.';
        setErrorCharts(errorMessage);
      } finally {
        setLoadingCharts(false);
      }
    };

    fetchData();
    // Fetch chart data on component mount and when submission is successful
    // Added onSubmissionSuccess to dependency array
  }, [onSubmissionSuccess]);

  // Effect to set the initial view and handle auto-opening the form based on user and data status
  useEffect(() => {
    // console.log(`Effect Triggered: user=${!!user}, existingTimelineData=${existingTimelineData === undefined ? 'loading' : (existingTimelineData === null ? 'null' : 'object')}`);

    // Only proceed if existingTimelineData is loaded (not undefined)
    if (existingTimelineData === undefined) {
      // console.log("Waiting for existingTimelineData...");
      // While waiting, ensure we show the charts/loading state by default.
      // Avoid setting state based on isFormModalOpen here.
      setIsWatching(true); // Default to watching charts while loading user data
      return;
    }

    // Keep it false, for now, we need to think of better UX for this.
    const shouldShowFormInitially = false;
    // console.log(`Decision: shouldShowFormInitially = ${shouldShowFormInitially}`);

    if (shouldShowFormInitially) {
      // User logged in, no data submitted yet -> Prepare and show form modal
      // console.log("Setting state for initial form display.");
      // Set form data *before* opening the modal
      setFormData({ ...initialBlankFormData, isExpressEntry: true });
      // Set view state *after* preparing data
      setIsWatching(false); // Hide charts background (apply blur)
      setIsFormModalOpen(true); // Open the modal
    } else {
      // User not logged in OR logged in with data -> Show charts
      // Ensure we are in the 'watching' state and the modal is closed,
      // unless the user manually opened it via toggle.
      // This branch runs when the condition `shouldShowFormInitially` is false.
      // console.log("Setting state for chart display.");
      setIsWatching(true); // Ensure charts are visible
      // We don't necessarily need to close the modal here, as the user might have opened it manually.
      // The handleToggleView and handleCloseFormModal handle manual interaction.
      // If `shouldShowFormInitially` was true on a previous render and is now false
      // (e.g., user logged out or submitted data), this will switch back to watching.
      // The modal state (`isFormModalOpen`) will be managed correctly by subsequent interactions
      // or if this effect runs again and `shouldShowFormInitially` becomes true.
    }

  // Dependencies: Only react to changes in the relevant props.
  // initialBlankFormData is stable due to useMemo and included as it's used inside.
  }, [user, existingTimelineData, initialBlankFormData]); // <-- Corrected dependencies


  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFormSubmitClick = () => {
    if (user) {
      // console.log("User logged in. Submitting timeline data directly:", formData);
      onDirectSubmit(formData);
      handleCloseFormModal(); // Close modal after submission
    } else {
      // console.log("User not logged in. Saving timeline data to localStorage:", formData);
      localStorage.setItem('pendingTimelineData', JSON.stringify(formData));
      onUpdateClick();
      handleCloseFormModal(); // Close modal after saving
    }
  };

  const handleToggleView = () => {
    if (isWatching) {
      let dataToSet = initialBlankFormData;
      if (existingTimelineData && typeof existingTimelineData === 'object') {
        // Pre-populate form with existing data, ensuring safe date conversion
        dataToSet = Object.keys(initialBlankFormData).reduce((acc, key) => {
          if (existingTimelineData[key] !== undefined) {
            // Check if it looks like a date field based on name
            if (key.toLowerCase().includes('date')) {
              const date = new Date(existingTimelineData[key]);
              acc[key] = !isNaN(date) ? date : null; // Set null if invalid date
            } else {
              acc[key] = existingTimelineData[key];
            }
          } else {
            acc[key] = initialBlankFormData[key]; // Use initial blank value if not present
          }
          return acc;
        }, {});

        // Derive frontend specific fields
        dataToSet.pnpProvince = existingTimelineData.province || '';
        dataToSet.isPnp = !!existingTimelineData.province;
      } else {
         // Use the stable initial blank form data
         dataToSet = initialBlankFormData;
      }

      setFormData(dataToSet);
      setIsWatching(false);
      setIsFormModalOpen(true);

    } else {
      setIsWatching(true);
      setIsFormModalOpen(false);
    }
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setIsWatching(true);
  };

  const handleCloseBanner = () => {
    setIsBannerVisible(false);
    navigator.clipboard.writeText(window.location.href)
      .then(() => {
        // Optional: Add a Snackbar notification here for user feedback
        // console.log('Page URL copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy URL: ', err);
      });
  };

  // --- ECharts Option Generators ---

  const streamChartOptions = useMemo(() => {
    if (!aggregateData) return {};
    const filteredData = aggregateData.filter(item =>
        selectedStreams.length === 0 || selectedStreams.includes(item._id)
    );

    const validData = filteredData.filter(item =>
        item._id && typeof item.avgProcessingTimeDays === 'number' && item.avgProcessingTimeDays > 0 && item.count > 0
    );
    const sortedData = [...validData].sort((a, b) => a.avgProcessingTimeDays - b.avgProcessingTimeDays);
    // No slice(0, 15) applied here as per original logic, showing all valid streams

    return {
      graphic: echarts.util.merge({}, getEchartsWatermark(), true),
      title: {
        text: 'Avg. Processing Time by Stream',
        left: 'center',
        textStyle: { fontSize: 16, fontWeight: 'normal' }
      },
      tooltip: {
        trigger: 'item',
        formatter: (params) => {
          const originalName = formatDrawName(params.name); // Format name here too
          const data = params.data;
          return `${originalName}<br/>Avg Time: ${data.value} days<br/>Submissions: ${data.count}`;
        }
      },
      grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
      xAxis: {
        type: 'value',
        name: 'Avg. Days',
        nameLocation: 'middle',
        nameGap: 30,
        axisLabel: { formatter: '{value} d' }
      },
      yAxis: {
        type: 'category',
        data: sortedData.map(item => item._id),
        axisLabel: {
          interval: 0,
          rotate: 0,
          formatter: (value) => formatDrawName(value),
          overflow: 'truncate',
          width: 69 // Keep width for truncation
        }
      },
      series: [{
        name: 'Avg Processing Time',
        type: 'bar',
        barMaxWidth: 30,
        data: sortedData.map(item => ({
           value: item.avgProcessingTimeDays || 0,
           count: item.count || 0
        })),
        label: { show: true, position: 'right', formatter: '{c} days' },
        itemStyle: { color: '#5470C6' }
      }]
    };
  }, [aggregateData, selectedStreams]);

  const voChartOptions = useMemo(() => {
    if (!voAggregateData) return {};

    const validData = voAggregateData.filter(item =>
        item._id && typeof item.avgProcessingTimeDays === 'number' && item.avgProcessingTimeDays > 0 && item.count > 0
    );
    const sortedData = [...validData].sort((a, b) => a.avgProcessingTimeDays - b.avgProcessingTimeDays);
    const displayData = sortedData.slice(0, 15); // Show top 15 fastest VOs

    return {
      graphic: echarts.util.merge({}, getEchartsWatermark(), true),
      title: {
        text: 'Avg. Processing Time by Visa Office (Top 15)',
         left: 'center',
         textStyle: { fontSize: 16, fontWeight: 'normal' }
      },
      tooltip: {
        trigger: 'item',
         formatter: (params) => {
           const data = params.data;
           return `${params.name}<br/>Avg Time: ${data.value} days<br/>Submissions: ${data.count}`;
         }
      },
      grid: { left: '3%', right: '4%', bottom: '10%', containLabel: true },
      xAxis: {
        type: 'value',
        name: 'Avg. Days',
        nameLocation: 'middle',
        nameGap: 30,
         axisLabel: { formatter: '{value} d' }
      },
      yAxis: {
        type: 'category',
        data: displayData.map(item => item._id || 'Unknown'),
        axisLabel: { interval: 0, fontSize: 10 }
      },
      // dataZoom: [ ... ], // DataZoom removed as only top 15 are shown
      series: [{
        name: 'Avg Processing Time',
        type: 'bar',
        barMaxWidth: 30,
        data: displayData.map(item => ({
           value: item.avgProcessingTimeDays || 0,
           count: item.count || 0
        })),
        label: { show: true, position: 'right', formatter: '{c} days', fontSize: 10 },
        itemStyle: { color: '#91CC75' }
      }]
    };
  }, [voAggregateData]);

  // Removed scatterChartOptions useMemo hook

  return (
    <Card>
      <CardContent sx={{ position: 'relative', pb: 2 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Community Timelines</Typography>
          {user ? (
             <Button
               variant="outlined"
               size="small"
               onClick={handleToggleView}
               disabled={existingTimelineData === undefined}
               sx={{ py: { xs: 0.5 }, px: { xs: 0.75 }, fontSize: { xs: '0.75rem' } }}
             >
               {existingTimelineData === undefined ? 'Loading...' : (isWatching ? 'Enter/Update My Timeline' : 'View Community Data')}
             </Button>
          ) : (
             <Button variant="outlined" size="small">
                Sign In to Enter Timeline ----- ^
             </Button>
          )}
        </Box>

        {/* Banner */}
        {isBannerVisible && (
          <Alert
            severity="info"
            icon={<CampaignIcon fontSize="inherit" />}
            onClose={handleCloseBanner}
            sx={{ mb: 2, mt: 1 }}
          >
            <AlertTitle>Help the Community Grow!</AlertTitle>
            Submit your timeline data and share this page with others (link copied when you close this banner!) to improve our insights.
          </Alert>
        )}

        {/* Filters */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
          <FormControl variant="outlined" fullWidth size="small">
              <InputLabel id="stream-filter-label">Filter by Stream(s)</InputLabel>
              <Select
                  labelId="stream-filter-label"
                  multiple
                  value={selectedStreams}
                  onChange={(event) => setSelectedStreams(event.target.value)}
                  label="Filter by Stream(s)"
                  renderValue={(selected) => selected.length === 0 ? <em>All Streams</em> : selected.map(s => formatDrawName(s)).join(', ')}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }} // Limit dropdown height
              >
                  {/* Option to deselect all */}
                  <MenuItem value={[]} onClick={() => setSelectedStreams([])}>
                     <em>All Streams</em>
                  </MenuItem>
                  {availableStreams.map((stream) => (
                      <MenuItem key={stream} value={stream}>
                          <Checkbox checked={selectedStreams.includes(stream)} />
                          <ListItemText primary={formatDrawName(stream)} />
                      </MenuItem>
                  ))}
              </Select>
          </FormControl>
          <FormControl variant="outlined" fullWidth size="small">
              <InputLabel id="vo-filter-label">Filter by Visa Office(s)</InputLabel>
              <Select
                  labelId="vo-filter-label"
                  multiple
                  value={selectedVOs}
                  onChange={(event) => setSelectedVOs(event.target.value)}
                  label="Filter by Visa Office(s)"
                  renderValue={(selected) => selected.length === 0 ? <em>All VOs</em> : selected.join(', ')}
                  MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }} // Limit dropdown height
                  disabled={availableVOs.length === 0} // Disable if no VOs found
              >
                  {/* Option to deselect all */}
                   <MenuItem value={[]} onClick={() => setSelectedVOs([])}>
                      <em>All VOs</em>
                   </MenuItem>
                  {availableVOs.map((vo) => (
                      <MenuItem key={vo} value={vo}>
                          <Checkbox checked={selectedVOs.includes(vo)} />
                          <ListItemText primary={vo} />
                      </MenuItem>
                  ))}
              </Select>
          </FormControl>
        </Box>

        {/* Container for potentially blurred content (Charts Area) */}
        <Box
          sx={{
            position: 'relative',
            filter: isFormModalOpen ? 'blur(4px)' : 'none',
            transition: 'filter 0.3s ease-in-out',
            pointerEvents: isFormModalOpen ? 'none' : 'auto',
            mt: 2,
          }}
        >
          {/* Loading/Error for chart data */}
          {loadingCharts && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}
          {errorCharts && !loadingCharts && ( // Only show error if not loading
            <Alert severity="error" sx={{ mt: 2 }}>{errorCharts}</Alert>
          )}

          {/* Community Data (Charts & Table) */}
          {!loadingCharts && !errorCharts && (
            <>
              {/* Card 1: Public Timelines Table */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  titleTypographyProps={{ variant: 'subtitle1' }}
                  title="Community Case Timelines"
                   sx={{ pb: 0 }} // Reduce padding bottom of header
                />
                <CardContent>
                  {/* Pass filters to the table */}
                  <PublicTimelineTable
                      data={individualTimelineData}
                      isLoading={loadingCharts}
                      error={errorCharts}
                      selectedStreams={selectedStreams}
                      selectedVOs={selectedVOs}
                      userTimelineId={existingTimelineData?._id} // Pass user's timeline ID
                  />
                  {/* Message moved inside PublicTimelineTable */}
                </CardContent>
              </Card>

              {/* Card 2: Case Status Timeline Chart */}
              
              <Card sx={{ mb: 3 }}>
                <CardHeader title="Case Status Timelines Chart" />
                <CardContent>
                  <CaseStatusTimelineChart
                      data={individualTimelineData}
                      isLoading={loadingCharts}
                      error={errorCharts}
                      selectedStreams={selectedStreams}
                      // selectedVOs={selectedVOs} // Pass VOs if needed by chart
                   />
                  {!loadingCharts && !errorCharts && (!individualTimelineData || individualTimelineData.length === 0) && (
                     <Typography sx={{ textAlign: 'center', p: 2, mt: 1 }}>No public timeline data available for the selected filters.</Typography>
                  )}
                </CardContent>
              </Card>

              {/* Card 3: Stream Chart */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  titleTypographyProps={{ variant: 'subtitle1' }} // Make header smaller
                  title="Avg. Processing Time by Stream"
                  action={
                    aggregateData && aggregateData.length > 0 && (
                      <IconButton
                        aria-label="download stream chart"
                        onClick={() => {
                          const instance = streamEchartsRef.current?.getEchartsInstance();
                          handleEchartsDownload(instance, 'Processing_Time_by_Stream', selectedStreams.length > 0 ? selectedStreams.join('-') : 'all-streams');
                        }}
                        size="small"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                  sx={{ pb: 0 }} // Reduce padding bottom of header
                />
                <CardContent>
                  {streamChartOptions?.series?.[0]?.data?.length > 0 ? ( // Check if there's actual data to display after filtering
                    <>
                      <Box sx={{ position: 'relative' }}>
                        <ReactECharts
                          ref={streamEchartsRef}
                          option={streamChartOptions}
                          style={{ height: isMobile ? '300px' : '350px', width: '100%' }}
                          notMerge={true}
                          lazyUpdate={true}
                        />
                      </Box>
                      <Typography variant="caption" display="block" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                        {processingTimeNote}
                      </Typography>
                    </>
                  ) : (
                    <Typography sx={{ textAlign: 'center', p: 2 }}>No stream data available for the selected filters.</Typography>
                  )}
                </CardContent>
              </Card>

              {/* Card 4: VO Chart */}
              <Card sx={{ mb: 3 }}>
                <CardHeader
                  titleTypographyProps={{ variant: 'subtitle1' }}
                  title="Avg. Processing Time by Visa Office (Top 15)"
                  action={
                    voChartOptions?.series?.[0]?.data?.length > 0 && ( // Check if there's actual data to display
                      <IconButton
                        aria-label="download vo chart"
                        onClick={() => {
                          const instance = voEchartsRef.current?.getEchartsInstance();
                          handleEchartsDownload(instance, 'Processing_Time_by_VO', 'top-15');
                        }}
                        size="small"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                   sx={{ pb: 0 }} // Reduce padding bottom of header
                />
                <CardContent>
                  {voChartOptions?.series?.[0]?.data?.length > 0 ? ( // Check if there's actual data to display
                   <>
                     <Box sx={{ position: 'relative' }}>
                       <ReactECharts
                         ref={voEchartsRef}
                         option={voChartOptions}
                         style={{ height: isMobile ? '300px' : '350px', width: '100%' }}
                         notMerge={true}
                         lazyUpdate={true}
                       />
                     </Box>
                     <Typography variant="caption" display="block" sx={{ textAlign: 'center', mt: 1, color: 'text.secondary' }}>
                       {processingTimeNote}
                     </Typography>
                   </>
                 ) : (
                    <Typography sx={{ textAlign: 'center', p: 2 }}>No visa office data available.</Typography>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </Box>

        {/* Timeline Form Modal */}
        <Modal
          open={isFormModalOpen}
          onClose={handleCloseFormModal}
          aria-labelledby="timeline-form-modal-title"
          aria-describedby="timeline-form-modal-description"
        >
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 600, md: 700 }, // Slightly wider on medium screens
            maxHeight: '90vh',
            overflowY: 'auto',
            bgcolor: 'background.paper',
            border: '1px solid #ccc', // Softer border
            borderRadius: '8px', // Rounded corners
            boxShadow: 24,
            p: { xs: 2, sm: 3, md: 4 }, // Responsive padding
          }}>
            <Typography id="timeline-form-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
              {existingTimelineData ? 'Update Your Immigration Timeline' : 'Enter Your Immigration Timeline'}
            </Typography>
            <TimelineForm formData={formData} onChange={handleFormChange} />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}> {/* Added gap */}
              <Button onClick={handleCloseFormModal} variant="outlined">Cancel</Button>
              <Button variant="contained" onClick={handleFormSubmitClick}>Submit Timeline</Button>
            </Box>
          </Box>
        </Modal>

      </CardContent>
    </Card>
  );
};

export default CrowdData;